import React, { FC, useMemo } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { AdvancedSelect } from '../../../../widgets/select';
import { ZSwitch } from '../../../../widgets/zswitch';
import {
    getModel3DParameterLabel,
    getModel3DParameterValuesWithDefaults,
    getSupportedModel3DParameters,
    isModel3DArrayParameter,
    isModel3DBooleanParameter,
    isModel3DIntegerParameter,
    isModel3DNumericParameter,
    Model3DParameterValue,
    Model3DParameterValues,
    ModelGeneration3DParameter
} from '../../modelGeneration3DParameters';
import { ModelGeneration3DModel } from '../../types';

interface ModelSelectOption {
    value: string;
    label: string;
}

interface Model3DSettingsSidebarProps {
    agentId: string | null | undefined;
    models: readonly ModelGeneration3DModel[];
    selectedModel?: string;
    onModelChange?: (agentId: string, model: string) => void;
    parameterValues?: Model3DParameterValues;
    onParameterValuesChange?: (agentId: string, model: string, values: Model3DParameterValues) => void;
}

export const getModelGeneration3DModelsForAgent = (models: readonly ModelGeneration3DModel[], agentId: string | null | undefined) =>
    agentId ? models.filter((model) => model.supportedModes.includes(agentId)) : [];

export const getDefaultModelGeneration3DModel = (models: readonly ModelGeneration3DModel[], agentId: string | null | undefined) => {
    const supportedModels = getModelGeneration3DModelsForAgent(models, agentId);
    return supportedModels.find((model) => model.isDefault)?.model ?? supportedModels[0]?.model;
};

export const isModelGeneration3DModelSupported = (models: readonly ModelGeneration3DModel[], agentId: string | null | undefined, modelId?: string) =>
    !!modelId && getModelGeneration3DModelsForAgent(models, agentId).some((model) => model.model === modelId);

const Sidebar = styled.aside`
    position: relative;
    z-index: 6;
    width: 280px;
    flex-shrink: 0;
    background: #ffffff;
    border-left: 1px solid ${(props) => props.theme.app.borderColor};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const SidebarHeader = styled.div`
    padding: 16px 20px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;

const SidebarTitle = styled.h3`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const SidebarContent = styled.div`
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const SettingGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SettingLabel = styled.label`
    font-size: 13px;
    font-weight: 400;
    color: ${(props) => props.theme.app.textColor};
`;

const EmptyMessage = styled.div`
    font-size: 12px;
    line-height: 1.5;
    color: #6b7280;
`;

const ParameterList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ParameterInput = styled.input`
    padding: 9px 10px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: ${(props) => props.theme.app.backgroundColor};
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
    line-height: 1.3;
    width: 100%;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
`;

const SwitchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const selectStyles = {
    control: (provided: Record<string, unknown>) => ({
        ...provided,
        borderRadius: 8
    }),
    menu: (provided: Record<string, unknown>) => ({
        ...provided,
        zIndex: 20
    })
};

interface ParameterSelectOption {
    value: string;
    label: string;
}

const formatParameterOptionLabel = (value: string) =>
    value
        .split(/[-_]/)
        .filter(Boolean)
        .map((part) => (part.length <= 3 ? part.toUpperCase() : `${part.charAt(0).toUpperCase()}${part.slice(1)}`))
        .join(' ');

export const Model3DSettingsSidebar: FC<Model3DSettingsSidebarProps> = ({
    agentId,
    models,
    selectedModel,
    onModelChange,
    parameterValues,
    onParameterValuesChange
}) => {
    const supportedModels = useMemo(() => getModelGeneration3DModelsForAgent(models, agentId), [models, agentId]);
    const defaultModel = useMemo(() => supportedModels.find((model) => model.isDefault) ?? supportedModels[0], [supportedModels]);
    const selected = supportedModels.find((model) => model.model === selectedModel) ?? defaultModel;
    const supportedParameters = useMemo(() => getSupportedModel3DParameters(selected, agentId), [selected, agentId]);
    const parameterValuesWithDefaults = useMemo(
        () => getModel3DParameterValuesWithDefaults(supportedParameters, parameterValues),
        [supportedParameters, parameterValues]
    );

    const modelOptions: ModelSelectOption[] = useMemo(
        () =>
            supportedModels.map((model) => ({
                value: model.model,
                label: model.modelName
            })),
        [supportedModels]
    );

    const updateParameterValue = (parameterName: string, value: Model3DParameterValue) => {
        if (!agentId || !selected) return;
        onParameterValuesChange?.(agentId, selected.model, {
            ...parameterValuesWithDefaults,
            [parameterName]: value
        });
    };

    const renderParameterControl = (parameter: ModelGeneration3DParameter) => {
        const value = parameterValuesWithDefaults[parameter.name];

        if (parameter.allowedValues.length > 0 && !isModel3DBooleanParameter(parameter)) {
            const options: ParameterSelectOption[] = parameter.allowedValues.map((allowedValue) => ({
                value: allowedValue,
                label: formatParameterOptionLabel(allowedValue)
            }));

            return (
                <AdvancedSelect
                    value={options.find((option) => option.value === String(value ?? '')) ?? options[0]}
                    onChange={(option: ParameterSelectOption | null) => updateParameterValue(parameter.name, option?.value ?? '')}
                    options={options}
                    isSearchable={false}
                    styles={selectStyles}
                />
            );
        }

        if (isModel3DBooleanParameter(parameter)) {
            return (
                <SwitchRow>
                    <SettingLabel as='span'>{T._(getModel3DParameterLabel(parameter.name), 'Admin')}</SettingLabel>
                    <ZSwitch checked={Boolean(value)} onChange={(checked) => updateParameterValue(parameter.name, checked)} />
                </SwitchRow>
            );
        }

        if (isModel3DArrayParameter(parameter)) {
            return (
                <ParameterInput
                    value={Array.isArray(value) ? value.join(', ') : String(value ?? '')}
                    onChange={(e) => updateParameterValue(parameter.name, e.currentTarget.value)}
                    placeholder={T._('Width, height, length', 'Admin')}
                />
            );
        }

        if (isModel3DNumericParameter(parameter)) {
            return (
                <ParameterInput
                    type='number'
                    value={value === undefined || value === null ? '' : String(value)}
                    min={parameter.minValue ?? undefined}
                    max={parameter.maxValue ?? undefined}
                    step={isModel3DIntegerParameter(parameter) ? 1 : 'any'}
                    onChange={(e) => updateParameterValue(parameter.name, e.currentTarget.value)}
                />
            );
        }

        return (
            <ParameterInput
                value={value === undefined || value === null ? '' : String(value)}
                onChange={(e) => updateParameterValue(parameter.name, e.currentTarget.value)}
            />
        );
    };

    return (
        <Sidebar>
            <SidebarHeader>
                <SidebarTitle>{T._('3D Settings', 'Admin')}</SidebarTitle>
            </SidebarHeader>
            <SidebarContent>
                <SettingGroup>
                    <SettingLabel>{T._('Model', 'Admin')}</SettingLabel>
                    {selected ? (
                        <>
                            <AdvancedSelect
                                value={modelOptions.find((option) => option.value === selected.model)}
                                onChange={(option: ModelSelectOption | null) => {
                                    if (agentId && option) {
                                        onModelChange?.(agentId, option.value);
                                    }
                                }}
                                options={modelOptions}
                                isSearchable={false}
                                styles={selectStyles}
                            />
                        </>
                    ) : (
                        <EmptyMessage>{T._('No generation models are available for this 3D agent.', 'Admin')}</EmptyMessage>
                    )}
                </SettingGroup>
                {selected && supportedParameters.length > 0 && (
                    <SettingGroup>
                        <ParameterList>
                            {supportedParameters.map((parameter) =>
                                isModel3DBooleanParameter(parameter) ? (
                                    <React.Fragment key={parameter.name}>{renderParameterControl(parameter)}</React.Fragment>
                                ) : (
                                    <SettingGroup key={parameter.name}>
                                        <SettingLabel>{T._(getModel3DParameterLabel(parameter.name), 'Admin')}</SettingLabel>
                                        {renderParameterControl(parameter)}
                                    </SettingGroup>
                                )
                            )}
                        </ParameterList>
                    </SettingGroup>
                )}
            </SidebarContent>
        </Sidebar>
    );
};

export default Model3DSettingsSidebar;
